import React from "react";

import { Stage } from "react-konva";

import { useScrubbing } from "../../hooks/useScrubbing";
import { VideoTrackLayer, VideoTrackLayerHandle } from "./VideoTrackLayer";

type Props = {
  width: number;
  height: number;
  pixelsPerSecond: number;
  scrollLeft: number;
  durationSeconds: number;
  maxScroll: number;
  setScrollLeft: (fn: (prev: number) => number) => void;
  onScrub: (t: number | null) => void;
  videoTrackRef: React.RefObject<VideoTrackLayerHandle | null>;
};

const arePropsEqual = (prev: Props, next: Props): boolean => {
  if (prev.width !== next.width || prev.height !== next.height) return false;
  if (prev.pixelsPerSecond !== next.pixelsPerSecond) return false;
  if (prev.scrollLeft !== next.scrollLeft) return false;
  return (
    prev.durationSeconds === next.durationSeconds &&
    prev.maxScroll === next.maxScroll &&
    prev.setScrollLeft === next.setScrollLeft &&
    prev.onScrub === next.onScrub &&
    prev.videoTrackRef === next.videoTrackRef
  );
};

export const ScrubBarCanvas: React.FC<Props> = React.memo(
  ({
    width,
    height,
    pixelsPerSecond,
    scrollLeft,
    durationSeconds,
    maxScroll,
    setScrollLeft,
    onScrub,
    videoTrackRef,
  }) => {
    const { stageRef, onPointerDown } = useScrubbing({
      pixelsPerSecond,
      scrollLeft,
      durationSeconds,
      width,
      maxScroll,
      setScrollLeft,
      onScrub,
    });

    if (!(width > 0) || !(height > 0)) return null;

    return (
      <Stage height={height} listening={true} ref={stageRef} width={width} onPointerDown={onPointerDown}>
        <VideoTrackLayer
          durationSeconds={durationSeconds}
          pixelsPerSecond={pixelsPerSecond}
          ref={videoTrackRef}
          scrollLeft={scrollLeft}
        />
      </Stage>
    );
  },
  arePropsEqual,
);

ScrubBarCanvas.displayName = "ScrubBarCanvas";
